import { createFileRoute, Link, useNavigate, redirect } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Eye, EyeOff, User, Building2, ArrowLeft, MapPin, Phone, Calendar, Mail, Lock, UserRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getStoredAuth } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

type SignupSearch = { type?: string };

export const Route = createFileRoute("/_auth/signup")({
  validateSearch: (s: Record<string, unknown>): SignupSearch => ({
    type: typeof s.type === "string" ? s.type : undefined,
  }),
  beforeLoad: () => {
    if (typeof window === "undefined") return;
    const auth = getStoredAuth();
    if (auth) {
      const dest = auth.role ? "/admin/dashboard" : "/dashboard";
      throw redirect({ to: dest as any });
    }
  },
  component: SignupPage,
});

type AccountType = "guest" | "venue_manager";
type Step = "select" | "form";

const OYO_CITIES = [
  "Ibadan", "Oyo", "Ogbomoso", "Iseyin", "Saki", "Igboho", "Kisi",
  "Eruwa", "Lanlate", "Igbo-Ora", "Fiditi", "Okeho",
];

function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();

  const initialType: AccountType = search.type === "venue_manager" ? "venue_manager" : "guest";
  const [step, setStep] = useState<Step>(search.type ? "form" : "select");
  const [accountType, setAccountType] = useState<AccountType>(initialType);
  const [animKey, setAnimKey] = useState(0);
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    password: "", confirm: "", dateOfBirth: "", location: "Ibadan",
  });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (field: string, value: string) => setForm(f => ({ ...f, [field]: value }));

  const pickType = (type: AccountType) => {
    if (type === "venue_manager") {
      navigate({ to: "/business-type" as any });
      return;
    }
    setAccountType(type);
    setStep("form");
    setAnimKey(k => k + 1);
  };

  const goBack = () => {
    setStep("select");
    setAnimKey(k => k + 1);
  };

  /* Live password requirement checks */
  const pwRules = {
    length:    form.password.length >= 8,
    uppercase: /[A-Z]/.test(form.password),
    digit:     /[0-9]/.test(form.password),
    match:     form.password.length > 0 && form.password === form.confirm,
  };
  const pwValid = pwRules.length && pwRules.uppercase && pwRules.digit;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pwValid) return toast.error("Password must be 8+ chars with an uppercase letter and a digit");
    if (form.password !== form.confirm) return toast.error("Passwords do not match");
    if (!form.phone.trim()) return toast.error("Phone number is required");
    if (!form.dateOfBirth) return toast.error("Date of birth is required");
    setLoading(true);
    try {
      await signup({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password,
        confirmPassword: form.confirm,
        dateOfBirth: form.dateOfBirth,
        location: form.location,
        role: accountType === "venue_manager" ? "VenueManager" : undefined,
      });
      if (accountType === "venue_manager") {
        toast.success("Venue account created! Welcome to OyoBooking.");
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        navigate({ to: "/list-your-property" as any });
      } else {
        toast.success("Account created! Welcome to OyoBooking.");
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        navigate({ to: "/dashboard" as any });
      }
    } catch (err) {
      const raw = err instanceof Error ? err.message : "Could not create account. Please try again.";
      const msg = raw.replace(/^HTTP \d+:\s*/i, "");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "h-12 rounded-xl border-border/80 bg-background px-4 text-base font-medium placeholder:text-muted-foreground/50 focus-visible:ring-2 focus-visible:ring-primary/30";

  return (
    <section className="mx-auto flex min-h-[calc(100vh-80px)] max-w-3xl flex-col justify-center px-4 py-10">
      <div className="w-full rounded-3xl border border-border/60 bg-card shadow-elegant overflow-hidden">

        {/* ── STEP 1: Pick account type ── */}
        {step === "select" && (
          <div key={`select-${animKey}`} className="p-8 sm:p-10">
            <div className="animate-fade-up mb-8 text-center">
              <Link to="/">
                <span className="font-display text-2xl font-bold text-primary">OyoBooking</span>
              </Link>
              <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-foreground">
                Join OyoBooking
              </h1>
              <p className="mt-2 text-base text-muted-foreground">How would you like to get started?</p>
            </div>

            <p className="animate-fade-in mb-4 text-center text-sm font-bold uppercase tracking-widest text-muted-foreground">
              Are you a?
            </p>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {([
                { type: "guest" as const, icon: User, title: "Guest",
                  desc: "Discover and book hotels, event venues & experiences across Oyo State.",
                  accent: "hover:border-primary hover:bg-primary/5",
                  iconBg: "bg-primary/10 text-primary", delay: "delay-150" },
                { type: "venue_manager" as const, icon: Building2, title: "Business Owner",
                  desc: "List your hotel, event centre or open space and start accepting bookings.",
                  accent: "hover:border-amber-500 hover:bg-amber-50/60",
                  iconBg: "bg-amber-100 text-amber-600", delay: "delay-300" },
              ] as const).map(opt => (
                <button
                  key={opt.type}
                  type="button"
                  onClick={() => pickType(opt.type)}
                  className={`animate-pop ${opt.delay} group flex flex-col items-center gap-4 rounded-2xl border-2 border-border p-7 text-center transition-all duration-200 ${opt.accent} active:scale-[0.97]`}
                >
                  <div className={`grid size-16 place-items-center rounded-2xl ${opt.iconBg} transition-transform duration-200 group-hover:scale-110`}>
                    <opt.icon className="size-8" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-foreground">{opt.title}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{opt.desc}</p>
                  </div>
                  <span className="mt-auto rounded-full border border-current px-5 py-1.5 text-xs font-semibold opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                    Continue →
                  </span>
                </button>
              ))}
            </div>

            <p className="animate-fade-in delay-450 mt-8 text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link to="/login" className="font-semibold text-primary hover:underline">Sign in</Link>
            </p>
          </div>
        )}

        {/* ── STEP 2: Registration form ── */}
        {step === "form" && (
          <div key={`form-${animKey}`} className="p-8 sm:p-10">
            {/* Back + header */}
            <div className="animate-slide-l mb-7">
              <button type="button" onClick={goBack}
                className="mb-5 flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="size-4" /> Back
              </button>
              <div className="flex items-center gap-3">
                <div className={`grid size-11 place-items-center rounded-xl ${accountType === "venue_manager" ? "bg-amber-100 text-amber-600" : "bg-primary/10 text-primary"}`}>
                  {accountType === "venue_manager" ? <Building2 className="size-5" /> : <User className="size-5" />}
                </div>
                <div>
                  <h1 className="font-display text-2xl font-extrabold leading-tight">
                    {accountType === "venue_manager" ? "Business Owner" : "Guest"}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {accountType === "venue_manager" ? "Set up your business account" : "Create your personal account"}
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Name row */}
              <div className="animate-fade-in delay-75 space-y-1.5">
                <Label className="text-sm font-bold flex items-center gap-1.5">
                  <UserRound className="size-3.5" /> First name <span className="text-rose-500">*</span>
                </Label>
                <Input required autoComplete="given-name" placeholder="Adaeze"
                  value={form.firstName} onChange={e => set("firstName", e.target.value)} className={inputCls} />
              </div>
              <div className="animate-fade-in delay-75 space-y-1.5">
                <Label className="text-sm font-bold flex items-center gap-1.5">
                  <UserRound className="size-3.5" /> Last name <span className="text-rose-500">*</span>
                </Label>
                <Input required autoComplete="family-name" placeholder="Okafor"
                  value={form.lastName} onChange={e => set("lastName", e.target.value)} className={inputCls} />
              </div>

              {/* Email */}
              <div className="animate-fade-in delay-150 space-y-1.5 sm:col-span-2">
                <Label className="text-sm font-bold flex items-center gap-1.5">
                  <Mail className="size-3.5" /> Email address <span className="text-rose-500">*</span>
                </Label>
                <Input required type="email" autoComplete="email" placeholder="you@example.com"
                  value={form.email} onChange={e => set("email", e.target.value)} className={inputCls} />
              </div>

              {/* Phone */}
              <div className="animate-fade-in delay-225 space-y-1.5">
                <Label className="text-sm font-bold flex items-center gap-1.5">
                  <Phone className="size-3.5" /> Phone number <span className="text-rose-500">*</span>
                </Label>
                <Input required type="tel" autoComplete="tel" placeholder="+234 800 000 0000"
                  value={form.phone} onChange={e => set("phone", e.target.value)} className={inputCls} />
              </div>

              {/* DOB */}
              <div className="animate-fade-in delay-300 space-y-1.5">
                <Label className="text-sm font-bold flex items-center gap-1.5">
                  <Calendar className="size-3.5" /> Date of birth <span className="text-rose-500">*</span>
                </Label>
                <Input required type="date" value={form.dateOfBirth}
                  onChange={e => set("dateOfBirth", e.target.value)}
                  className={inputCls} />
              </div>

              {/* City */}
              <div className="animate-fade-in delay-300 space-y-1.5">
                <Label htmlFor="signup-city" className="text-sm font-bold flex items-center gap-1.5">
                  <MapPin className="size-3.5" /> City <span className="text-rose-500">*</span>
                </Label>
                <select id="signup-city" required value={form.location} onChange={e => set("location", e.target.value)}
                  title="Select City"
                  className={`${inputCls} w-full appearance-none shadow-sm`}>
                  {OYO_CITIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Password */}
              <div className="animate-fade-in delay-375 space-y-1.5 sm:col-span-2">
                <Label className="text-sm font-bold flex items-center gap-1.5">
                  <Lock className="size-3.5" /> Password <span className="text-rose-500">*</span>
                </Label>
                <div className="relative">
                  <Input required type={showPw ? "text" : "password"} autoComplete="new-password"
                    placeholder="Min 8 chars, 1 uppercase, 1 digit"
                    value={form.password} onChange={e => set("password", e.target.value)}
                    className={`${inputCls} pr-12`} />
                  <button type="button" onClick={() => setShowPw(s => !s)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {showPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
              </div>

              {/* Password requirements */}
              {form.password.length > 0 && (
                <div className="animate-fade-in sm:col-span-2 -mt-1 grid grid-cols-2 gap-x-4 gap-y-1 rounded-xl border border-border/60 bg-accent/40 px-4 py-3 text-xs">
                  {([
                    [pwRules.length,    "8+ characters"],
                    [pwRules.uppercase, "One uppercase letter"],
                    [pwRules.digit,     "One digit (0-9)"],
                    [pwRules.match,     "Passwords match"],
                  ] as [boolean, string][]).map(([ok, label]) => (
                    <span key={label} className={`flex items-center gap-1.5 font-medium ${ok ? "text-green-600" : "text-muted-foreground"}`}>
                      <span className={`size-1.5 rounded-full ${ok ? "bg-green-500" : "bg-muted-foreground/40"}`} />
                      {label}
                    </span>
                  ))}
                </div>
              )}

              {/* Confirm */}
              <div className="animate-fade-in delay-450 space-y-1.5 sm:col-span-2">
                <Label className="text-sm font-bold flex items-center gap-1.5">
                  <Lock className="size-3.5" /> Confirm password <span className="text-rose-500">*</span>
                </Label>
                <Input required type="password" autoComplete="new-password" placeholder="Re-enter your password"
                  value={form.confirm} onChange={e => set("confirm", e.target.value)} className={inputCls} />
              </div>

              <div className="animate-fade-in delay-500 pt-1 sm:col-span-2">
                <Button type="submit" disabled={loading} size="lg" className="h-12 w-full rounded-xl text-base font-bold">
                  {loading ? "Creating account…" : accountType === "venue_manager" ? "Create Business Account" : "Create Account"}
                </Button>
              </div>
            </form>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link to="/login" className="font-semibold text-primary hover:underline">Sign in</Link>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
